import React, { useState, useEffect, useMemo } from 'react';
import { collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc, query, orderBy } from 'firebase/firestore';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Plus, ChevronLeft, ChevronRight, CalendarDays, X, GripVertical, MapPin, Trash2 } from 'lucide-react';
import { db } from '../../firebase';

function SortableJobCard({ job, startEditingJob, handleDeleteJob, onOpenMap, address }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: job.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const hasPrice = job.price != null && job.price !== '' && !isNaN(parseFloat(job.price)) && parseFloat(job.price) > 0;
  const statusLabel = { scheduled: 'Scheduled', 'in-progress': 'In progress', completed: 'Done', cancelled: 'Cancelled' }[job.status] || job.status;

  const statusColor = job.status === 'completed' ? 'border-l-green-500' :
    job.status === 'in-progress' ? 'border-l-blue-500' :
    job.status === 'cancelled' ? 'border-l-red-400' : 'border-l-amber-400';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-0 rounded-xl border border-secondary-200 bg-white shadow-sm overflow-hidden min-h-[48px] sm:min-h-[56px] border-l-4 ${statusColor} ${isDragging ? 'opacity-80 shadow-lg ring-2 ring-primary-300 z-10' : ''}`}
    >
      <span
        className="flex items-center justify-center w-9 sm:w-10 shrink-0 self-stretch bg-secondary-50 text-secondary-400 hover:text-secondary-600 cursor-grab active:cursor-grabbing touch-manipulation border-r border-secondary-100"
        {...listeners}
        {...attributes}
        aria-label="Drag to reorder"
      >
        <GripVertical className="w-4 h-4 sm:w-5 sm:h-5" />
      </span>
      <button
        type="button"
        onClick={() => startEditingJob(job)}
        className="flex-1 min-w-0 text-left py-3 pl-2 pr-2 sm:p-4 hover:bg-primary-50/50 active:bg-primary-100/50 transition-colors flex flex-col justify-center"
      >
        {/* Mobile: address + status pill. Desktop: address + service + price + status */}
        <div className="flex items-center gap-2">
          <p className="font-semibold text-secondary-900 truncate text-base sm:text-[15px] leading-tight flex-1 min-w-0" title={address || job.customerName}>
            {address || job.customerName}
          </p>
          <span className={`sm:hidden shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full ${
            job.status === 'completed' ? 'bg-green-100 text-green-700' :
            job.status === 'in-progress' ? 'bg-blue-100 text-blue-700' :
            job.status === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
          }`}>
            {statusLabel}
          </span>
        </div>
        <p className="text-xs sm:text-sm text-secondary-500 mt-0.5 truncate hidden sm:block">
          {job.serviceType}
          {hasPrice && <span className="text-secondary-500"> · ${parseFloat(job.price).toFixed(2)}</span>}
        </p>
        <span className={`hidden sm:inline-block mt-2 text-xs font-medium px-2 py-0.5 rounded-full ${
          job.status === 'completed' ? 'bg-green-100 text-green-800' :
          job.status === 'in-progress' ? 'bg-blue-100 text-blue-800' :
          job.status === 'cancelled' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'
        }`}>
          {statusLabel}
        </span>
      </button>
      {/* Mobile: name + Map (tap name to edit). Desktop: + Map + Del icons */}
      {onOpenMap && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onOpenMap(job); }}
          className="shrink-0 inline-flex items-center gap-1.5 sm:gap-0 sm:flex-col sm:justify-center py-2 pr-3 pl-2 sm:p-2.5 text-primary-600 hover:bg-primary-50 font-medium sm:font-normal sm:text-secondary-500 sm:hover:text-primary-600 border-l border-secondary-100 min-h-[44px] sm:min-h-0"
          title="Open map"
        >
          <MapPin className="w-5 h-5 shrink-0" />
          <span className="text-sm sm:hidden">Map</span>
        </button>
      )}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); handleDeleteJob(job.id); }}
        className="hidden sm:flex p-2.5 text-secondary-400 hover:bg-red-50 hover:text-red-600 transition-colors min-h-[44px] min-w-[44px] items-center justify-center border-l border-secondary-100 shrink-0"
        title="Delete"
      >
        <Trash2 className="w-5 h-5" />
      </button>
    </div>
  );
}

const DailyJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [showAddJob, setShowAddJob] = useState(false);
  const [showDayModal, setShowDayModal] = useState(false);
  const [showAddFormInDayModal, setShowAddFormInDayModal] = useState(false);
  const [dayModalCustomerId, setDayModalCustomerId] = useState('');
  const [editingJob, setEditingJob] = useState(null);
  const [editingCustomerInfo, setEditingCustomerInfo] = useState({});
  const [quickAdd, setQuickAdd] = useState({ customerId: '', serviceType: '', price: '' });
  const [quickAddCustomerSearch, setQuickAddCustomerSearch] = useState('');
  const [customerDayModal, setCustomerDayModal] = useState(null);
  const [customerDayAdd, setCustomerDayAdd] = useState({ serviceType: '', price: '' });
  const [mapModalJob, setMapModalJob] = useState(null);
  const [mapModalAddress, setMapModalAddress] = useState('');
  const [mapModalSaving, setMapModalSaving] = useState(false);
  const [newJob, setNewJob] = useState({
    services: [{ serviceType: '', price: '' }],
    serviceType: '',
    description: '',
    scheduledDate: selectedDate,
    price: '',
    status: 'scheduled',
    notes: ''
  });

  // Real-time listeners
  useEffect(() => {
    const unsubscribers = [];

    // Jobs listener
    const jobsQuery = query(
      collection(db, 'jobs'),
      orderBy('scheduledDate', 'desc')
    );
    const jobsUnsub = onSnapshot(jobsQuery, (snapshot) => {
      const jobsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setJobs(jobsData);
    });
    unsubscribers.push(jobsUnsub);

    // Customers listener
    const customersUnsub = onSnapshot(collection(db, 'customers'), (snapshot) => {
      const customersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCustomers(customersData);
    });
    unsubscribers.push(customersUnsub);

    return () => unsubscribers.forEach(unsub => unsub());
  }, []);

  // Sync editable address when map modal opens
  useEffect(() => {
    if (!mapModalJob) return;
    const customer = customers.find(c => c.id === mapModalJob.customerId);
    const addr = customer && customer.address
      ? (typeof customer.address === 'object'
        ? [customer.address.street, customer.address.city, customer.address.state, customer.address.zipCode].filter(Boolean).join(', ')
        : customer.address)
      : '';
    setMapModalAddress(addr);
  }, [mapModalJob, customers]);

  // Filter jobs for selected date and sort by order (for drag-and-drop)
  const selectedDateJobs = useMemo(() => {
    const list = jobs.filter(job => job.scheduledDate === selectedDate);
    return list.slice().sort((a, b) => {
      const orderA = a.order != null ? a.order : 999999;
      const orderB = b.order != null ? b.order : 999999;
      if (orderA !== orderB) return orderA - orderB;
      return (a.createdAt?.seconds ?? 0) - (b.createdAt?.seconds ?? 0);
    });
  }, [jobs, selectedDate]);

  // Get address string and map URL for a customer (for phone view)
  const getCustomerAddress = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    if (!customer) return { address: '', mapUrl: null };
    let addressStr = '';
    if (typeof customer.address === 'object' && customer.address) {
      addressStr = [customer.address.street, customer.address.city, customer.address.state, customer.address.zipCode]
        .filter(Boolean).join(', ').trim();
    } else if (customer.address) {
      addressStr = String(customer.address);
    } else if (customer.city || customer.state || customer.zipCode) {
      addressStr = [customer.city, customer.state, customer.zipCode].filter(Boolean).join(', ').trim();
    }
    const mapUrl = addressStr
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressStr)}`
      : null;
    return { address: addressStr, mapUrl };
  };

  // Build Google Maps URL with all job addresses for the day (as waypoints) and a list of labels for the legend
  const MAP_LABELS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
  const getMapStopsAndUrl = () => {
    const stops = [];
    const seen = new Set();
    selectedDateJobs.forEach((job) => {
      const { address } = getCustomerAddress(job.customerId);
      if (address && !seen.has(address)) {
        seen.add(address);
        const label = MAP_LABELS[stops.length] || String(stops.length + 1);
        stops.push({ label, customerName: job.customerName || 'Unknown', address });
      }
    });
    if (stops.length === 0) return { url: null, stops: [] };
    const path = stops.map(s => encodeURIComponent(s.address)).join('/');
    return { url: `https://www.google.com/maps/dir/${path}`, stops };
  };

  const addServiceLine = () => {
    setNewJob(prev => ({ ...prev, services: [...(prev.services || [{ serviceType: '', price: '' }]), { serviceType: '', price: '' }] }));
  };
  const removeServiceLine = (index) => {
    setNewJob(prev => {
      const next = (prev.services || []).filter((_, i) => i !== index);
      return { ...prev, services: next.length ? next : [{ serviceType: '', price: '' }] };
    });
  };
  const updateServiceLine = (index, field, value) => {
    setNewJob(prev => ({
      ...prev,
      services: (prev.services || []).map((line, i) => i === index ? { ...line, [field]: value } : line)
    }));
  };

  const handleAddJob = async (e) => {
    e.preventDefault();
    if (selectedCustomers.length === 0) {
      alert('Please select at least one customer.');
      return;
    }
    const lines = (newJob.services || []).filter(l => l.serviceType && String(l.serviceType).trim());
    if (lines.length === 0) {
      alert('Please add at least one service.');
      return;
    }
    try {
      let orderIndex = selectedDateJobs.length;
      const jobPromises = [];
      for (const customerId of selectedCustomers) {
        const selectedCustomer = customers.find(c => c.id === customerId);
        const customerName = selectedCustomer ? selectedCustomer.fullName || `${selectedCustomer.firstName || ''} ${selectedCustomer.lastName}`.trim() : '';
        for (const line of lines) {
          let jobPrice = parseFloat(line.price);
          if (isNaN(jobPrice) || jobPrice < 0) {
            const def = selectedCustomer?.services?.[line.serviceType];
            jobPrice = def != null ? parseFloat(def) : 0;
          }
          jobPromises.push(addDoc(collection(db, 'jobs'), {
            customerId,
            customerName,
            serviceType: line.serviceType,
            description: newJob.description,
            scheduledDate: newJob.scheduledDate,
            price: jobPrice,
            status: newJob.status,
            notes: newJob.notes || '',
            order: orderIndex++,
            createdAt: new Date()
          }));
        }
      }
      await Promise.all(jobPromises);
      setShowAddJob(false);
      setSelectedCustomers([]);
      setCustomerSearch('');
      setNewJob({
        services: [{ serviceType: '', price: '' }],
        serviceType: '',
        description: '',
        scheduledDate: selectedDate,
        price: '',
        status: 'scheduled',
        notes: ''
      });
    } catch (error) {
      console.error('Error adding jobs:', error);
      alert('Failed to add jobs. Please try again.');
    }
  };

  // Handle editing existing job
  const handleEditJob = async (e) => {
    e.preventDefault();

    try {
      const selectedCustomer = customers.find(c => c.id === editingJob.customerId);
      
      // Get pricing from customer data or use manual price
      let jobPrice = parseFloat(newJob.price) || 0;
      if (selectedCustomer && selectedCustomer.services && selectedCustomer.services[newJob.serviceType]) {
        jobPrice = selectedCustomer.services[newJob.serviceType];
      }

      const jobData = {
        serviceType: newJob.serviceType,
        description: newJob.description,
        scheduledDate: newJob.scheduledDate,
        price: jobPrice,
        status: newJob.status,
        notes: newJob.notes || '',
        updatedAt: new Date()
      };
      if (newJob.status === 'completed') {
        jobData.completedAt = new Date();
      }

      await updateDoc(doc(db, 'jobs', editingJob.id), jobData);

      // Also update customer info if changed
      if (editingJob.customerId && Object.keys(editingCustomerInfo).length > 0) {
        const customerUpdateData = {
          firstName: editingCustomerInfo.firstName,
          lastName: editingCustomerInfo.lastName,
          email: editingCustomerInfo.email,
          phone: editingCustomerInfo.phone,
          city: editingCustomerInfo.city,
          address: editingCustomerInfo.address,
          updatedAt: new Date()
        };
        await updateDoc(doc(db, 'customers', editingJob.customerId), customerUpdateData);
      }

      setShowAddJob(false);
      setEditingJob(null);
      setEditingCustomerInfo({});
      setNewJob({
        serviceType: '',
        description: '',
        scheduledDate: selectedDate,
        price: '',
        status: 'scheduled',
        notes: ''
      });
    } catch (error) {
      console.error('Error updating job:', error);
      alert('Failed to update job. Please try again.');
    }
  };

  // Start editing a job
  const startEditingJob = (job) => {
    setCustomerDayModal(null);
    setEditingJob(job);
    const customer = customers.find(c => c.id === job.customerId);
    setEditingCustomerInfo({
      firstName: customer?.firstName || '',
      lastName: customer?.lastName || '',
      email: customer?.email || '',
      phone: customer?.phone || '',
      address: typeof customer?.address === 'object' ? `${customer.address.street || ''}, ${customer.address.city || ''}, ${customer.address.state || ''} ${customer.address.zipCode || ''}`.trim() : (customer?.address || ''),
      city: customer?.city || ''
    });
    setNewJob({
      services: [{ serviceType: job.serviceType || '', price: job.price ?? '' }],
      serviceType: job.serviceType || '',
      description: job.description || '',
      scheduledDate: job.scheduledDate || selectedDate,
      price: job.price || '',
      status: job.status || 'scheduled',
      notes: job.notes || ''
    });
    setShowAddJob(true);
  };

  // Get customer pricing for a service type
  const getCustomerPricing = (customerId, serviceType) => {
    const customer = customers.find(c => c.id === customerId);
    return customer && customer.services ? customer.services[serviceType] || 0 : 0;
  };

  // Handle status update (set completedAt for month-end / QuickBooks reporting)
  const updateJobStatus = async (jobId, newStatus) => {
    try {
      const update = {
        status: newStatus,
        updatedAt: new Date()
      };
      if (newStatus === 'completed') {
        update.completedAt = new Date();
      }
      await updateDoc(doc(db, 'jobs', jobId), update);
    } catch (error) {
      console.error('Error updating job status:', error);
      alert('Failed to update job status.');
    }
  };

  const SERVICE_OPTIONS = ['Lawn Mowing', 'Hedge Trimming', 'Tree Trimming', 'Fertilizing', 'Pre-Emergent / Fertilize', 'Weed Control', 'Aeration', 'Seeding', 'Lighting', 'Planting', 'Other'];

  const handleQuickAddJob = async (e) => {
    e.preventDefault();
    if (!quickAdd.customerId || !quickAdd.serviceType) {
      alert('Please select a customer and service type.');
      return;
    }
    const customer = customers.find(c => c.id === quickAdd.customerId);
    const customerName = customer ? (customer.fullName || `${customer.firstName || ''} ${customer.lastName || ''}`.trim()) : '';
    let price = parseFloat(quickAdd.price) || 0;
    if (customer?.services?.[quickAdd.serviceType]) price = customer.services[quickAdd.serviceType];
    if (quickAdd.price !== '' && parseFloat(quickAdd.price) >= 0) price = parseFloat(quickAdd.price);
    try {
      await addDoc(collection(db, 'jobs'), {
        customerId: quickAdd.customerId,
        customerName,
        serviceType: quickAdd.serviceType,
        description: '',
        scheduledDate: selectedDate,
        price,
        status: 'scheduled',
        notes: '',
        order: selectedDateJobs.length,
        createdAt: new Date()
      });
      // Keep same customer so you can add another job for them; clear only service and price
      setQuickAdd(prev => ({ ...prev, serviceType: '', price: '' }));
    } catch (err) {
      console.error('Error adding job:', err);
      alert('Failed to add job.');
    }
  };

  const customerDayJobs = customerDayModal
    ? selectedDateJobs.filter(j => j.customerId === customerDayModal.customerId)
    : [];

  const handleAddJobInCustomerDayModal = async (e) => {
    e.preventDefault();
    if (!customerDayModal || !customerDayAdd.serviceType) return;
    const price = (customerDayAdd.price !== '' && parseFloat(customerDayAdd.price) >= 0)
      ? parseFloat(customerDayAdd.price)
      : (getCustomerPricing(customerDayModal.customerId, customerDayAdd.serviceType) || 0);
    try {
      await addDoc(collection(db, 'jobs'), {
        customerId: customerDayModal.customerId,
        customerName: customerDayModal.customerName,
        serviceType: customerDayAdd.serviceType,
        description: '',
        scheduledDate: selectedDate,
        price,
        status: 'scheduled',
        notes: '',
        order: selectedDateJobs.length,
        createdAt: new Date()
      });
      setCustomerDayAdd({ serviceType: '', price: '' });
    } catch (err) {
      console.error('Error adding job:', err);
      alert('Failed to add job.');
    }
  };

  // Delete job
  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Remove this job from the day?')) return;
    try {
      await deleteDoc(doc(db, 'jobs', jobId));
    } catch (error) {
      console.error('Error deleting job:', error);
      alert('Failed to delete job.');
    }
  };

  // Reorder jobs after drag (persist order to Firestore)
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = selectedDateJobs.findIndex((j) => j.id === active.id);
    const newIndex = selectedDateJobs.findIndex((j) => j.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = [...selectedDateJobs];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);
    try {
      await Promise.all(
        reordered.map((job, index) =>
          updateDoc(doc(db, 'jobs', job.id), { order: index })
        )
      );
    } catch (err) {
      console.error('Error reordering jobs:', err);
      alert('Failed to save order.');
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  // Add a single job from the day modal (for the modal's date)
  const handleAddJobFromDayModal = async (e) => {
    e.preventDefault();
    if (!dayModalCustomerId) {
      alert('Please select a customer.');
      return;
    }
    const customer = customers.find(c => c.id === dayModalCustomerId);
    const customerName = customer ? (customer.fullName || `${customer.firstName || ''} ${customer.lastName || ''}`.trim()) : '';
    let jobPrice = parseFloat(newJob.price) || 0;
    if (customer?.services?.[newJob.serviceType]) jobPrice = customer.services[newJob.serviceType];
    try {
      await addDoc(collection(db, 'jobs'), {
        customerId: dayModalCustomerId,
        customerName,
        serviceType: newJob.serviceType,
        description: newJob.description,
        scheduledDate: selectedDate,
        price: jobPrice,
        status: newJob.status,
        notes: newJob.notes || '',
        order: selectedDateJobs.length,
        createdAt: new Date()
      });
      setShowAddFormInDayModal(false);
      setDayModalCustomerId('');
      setNewJob(prev => ({ ...prev, serviceType: '', description: '', price: '', notes: '' }));
    } catch (err) {
      console.error('Error adding job:', err);
      alert('Failed to add job.');
    }
  };

  // Parse YYYY-MM-DD as local date (avoid UTC timezone shift)
  const parseLocalDate = (dateString) => {
    const [y, m, d] = dateString.split('-').map(Number);
    return new Date(y, m - 1, d);
  };
  const formatDate = (dateString) => {
    const date = parseLocalDate(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const toLocalDateString = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  const getJobsForCalendarDate = (date) => {
    if (!date) return [];
    const dateString = toLocalDateString(date);
    return jobs.filter(job => job.scheduledDate === dateString);
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  const selectDate = (date) => {
    if (date) {
      const dateString = toLocalDateString(date);
      setSelectedDate(dateString);
      setNewJob(prev => ({ ...prev, scheduledDate: dateString }));
      setShowDayModal(true);
      setShowAddFormInDayModal(false);
    }
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date) => {
    if (!date) return false;
    const dateString = toLocalDateString(date);
    return dateString === selectedDate;
  };

  const hasJobs = (date) => {
    if (!date) return false;
    return getJobsForCalendarDate(date).length > 0;
  };

  // Filter customers based on search
  const filteredCustomers = customers.filter(customer => {
    const searchTerm = customerSearch.toLowerCase();
    const fullName = (customer.fullName || `${customer.firstName || ''} ${customer.lastName || ''}`).toLowerCase();
    const email = (customer.email || '').toLowerCase();
    return fullName.includes(searchTerm) || email.includes(searchTerm);
  });

  // Handle customer selection
  const handleCustomerToggle = (customerId) => {
    setSelectedCustomers(prev =>
      prev.includes(customerId)
        ? prev.filter(id => id !== customerId)
        : [...prev, customerId]
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-0 sm:px-6 lg:px-8 py-4 sm:py-8">
      {/* Jobs for the day - main dashboard content */}
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div className="px-4 sm:px-0">
            <h1 className="text-xl sm:text-2xl font-bold text-secondary-900">
              Jobs for {formatDate(selectedDate)}
            </h1>
            <p className="text-secondary-600 text-sm mt-1">
              {selectedDateJobs.length} job{selectedDateJobs.length !== 1 ? 's' : ''} scheduled
            </p>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto">
            {(() => {
              const { url, stops } = getMapStopsAndUrl();
              if (!url) return null;
              return (
                <>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary whitespace-nowrap min-h-[44px] px-4 touch-manipulation inline-flex items-center gap-2 w-full sm:w-auto justify-center"
                  >
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-600">
                      <MapPin className="w-5 h-5" strokeWidth={2.5} />
                    </span>
                    View all on map
                  </a>
                  {stops.length > 0 && (
                    <p className="text-xs text-secondary-600 flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="font-medium text-secondary-700">On map:</span>
                      {stops.map(({ label, customerName }) => (
                        <span key={label} className="inline-flex items-center justify-center w-5 h-5 rounded bg-primary-100 text-primary-700 font-bold text-[10px]">{label}</span>
                      ))}
                    </p>
                  )}
                </>
              );
            })()}
          </div>
        </div>

        {/* Quick add one job: search customer + Service + Price */}
        <form onSubmit={handleQuickAddJob} className="mb-4 px-4 sm:px-0">
          <div className="card p-3 sm:p-4">
            <p className="text-xs font-medium text-secondary-500 uppercase tracking-wider mb-3">Quick add job <span className="font-normal normal-case text-secondary-400 hidden sm:inline">— search customer, pick service & price</span></p>
            <div className="mb-3">
              <label className="block text-xs font-medium text-secondary-600 mb-1">Search customer</label>
              <input
                type="text"
                placeholder="Type name or email to find customer..."
                value={quickAddCustomerSearch}
                onChange={(e) => setQuickAddCustomerSearch(e.target.value)}
                className="input w-full text-sm py-2"
              />
            </div>
            <div className="flex flex-wrap gap-2 sm:gap-3 items-end">
              <div className="flex-1 min-w-[140px]">
                <label className="block text-xs font-medium text-secondary-600 mb-1">Customer</label>
                <select
                  value={quickAdd.customerId}
                  onChange={(e) => setQuickAdd(prev => ({ ...prev, customerId: e.target.value }))}
                  className="input w-full text-sm py-2"
                >
                  <option value="">Select customer</option>
                  {(() => {
                    const q = (quickAddCustomerSearch || '').trim().toLowerCase();
                    const list = !q
                      ? customers
                      : customers.filter((c) => {
                          const name = (c.fullName || `${c.firstName || ''} ${c.lastName || ''}`.trim() || '').toLowerCase();
                          const email = (c.email || '').toLowerCase();
                          return name.includes(q) || email.includes(q);
                        });
                    return list.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.fullName || `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'Unknown'}
                      </option>
                    ));
                  })()}
                </select>
              </div>
              <div className="flex-1 min-w-[120px]">
                <label className="block text-xs font-medium text-secondary-600 mb-1">Service</label>
                <select
                  value={quickAdd.serviceType}
                  onChange={(e) => setQuickAdd(prev => ({ ...prev, serviceType: e.target.value }))}
                  className="input w-full text-sm py-2"
                >
                  <option value="">Select service</option>
                  {SERVICE_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="w-24">
                <label className="block text-xs font-medium text-secondary-600 mb-1">Price</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0"
                  value={quickAdd.price}
                  onChange={(e) => setQuickAdd(prev => ({ ...prev, price: e.target.value }))}
                  className="input w-full text-sm py-2"
                />
              </div>
              <button type="submit" className="btn-primary py-2 px-4 whitespace-nowrap">
                Add job
              </button>
            </div>
          </div>
        </form>

        {selectedDateJobs.length === 0 ? (
          <div className="card">
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-secondary-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                <CalendarDays className="w-8 h-8 text-secondary-400" />
              </div>
              <h3 className="text-lg font-semibold text-secondary-900 mb-2">No jobs for this day</h3>
              <p className="text-secondary-600 mb-4">
                Add a job or pick another day from the calendar below.
              </p>
              <button onClick={() => setShowAddJob(true)} className="btn-primary">
                Add job
              </button>
            </div>
          </div>
        ) : (
          <div className="card overflow-hidden">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <div className="p-3 sm:p-4 space-y-2 sm:space-y-3">
                <SortableContext
                  items={selectedDateJobs.map((j) => j.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {selectedDateJobs.map((job) => {
                    const { address } = getCustomerAddress(job.customerId);
                    return (
                      <SortableJobCard
                        key={job.id}
                        job={job}
                        startEditingJob={startEditingJob}
                        handleDeleteJob={handleDeleteJob}
                        onOpenMap={(j) => setMapModalJob(j)}
                        address={address}
                      />
                    );
                  })}
                </SortableContext>
              </div>
            </DndContext>
            {/* Price breakdown by service type */}
            {(() => {
              const byService = {};
              let dayTotal = 0;
              selectedDateJobs.forEach((job) => {
                const svc = job.serviceType || 'Other';
                if (!byService[svc]) byService[svc] = { count: 0, total: 0 };
                const p = parseFloat(job.price) || 0;
                byService[svc].count += 1;
                byService[svc].total += p;
                dayTotal += p;
              });
              const entries = Object.entries(byService).sort((a, b) => b[1].total - a[1].total);
              return (
                <div className="px-3 py-3 border-t border-secondary-200 bg-secondary-50/50">
                  <p className="text-xs font-semibold text-secondary-600 uppercase tracking-wider mb-2 hidden sm:block">Price breakdown</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm hidden sm:flex">
                    {entries.map(([svc, { count, total }]) => (
                      <span key={svc} className="text-secondary-700">
                        {svc}: <span className="font-medium text-secondary-900">{count} job{count !== 1 ? 's' : ''}</span>, <span className="font-semibold text-green-700">${total.toFixed(2)}</span>
                      </span>
                    ))}
                  </div>
                  <p className="sm:mt-2 sm:pt-2 sm:border-t sm:border-secondary-200 text-sm font-semibold text-secondary-900">
                    Day total: <span className="text-green-700">${dayTotal.toFixed(2)}</span>
                  </p>
                </div>
              );
            })()}
            <p className="px-3 py-2 text-xs text-secondary-500 border-t border-secondary-100">
              {selectedDateJobs.length} job{selectedDateJobs.length !== 1 ? 's' : ''}
            </p>
          </div>
        )}
      </div>

      {/* Calendar - pick a day */}
      <div className="mb-6 sm:mb-8 px-4 sm:px-0">
        <h2 className="text-lg font-semibold text-secondary-900 mb-4">Pick a day</h2>
        <div>
          {/* Calendar */}
          <div className="w-full">
            <div className="card overflow-hidden">
              <div className="p-3 sm:p-6">
                {/* Calendar Header */}
                <div className="flex items-center justify-between mb-6">
                  <button
                    onClick={() => navigateMonth(-1)}
                    className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5 text-secondary-600" />
                  </button>

                  <h2 className="text-xl font-semibold text-secondary-900">
                    {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </h2>

                  <button
                    onClick={() => navigateMonth(1)}
                    className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
                  >
                    <ChevronRight className="w-5 h-5 text-secondary-600" />
                  </button>
                </div>

                {/* Days of Week Header */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} className="p-2 text-center text-sm font-medium text-secondary-500">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-1">
                  {getDaysInMonth(currentMonth).map((date, index) => (
                    <button
                      key={index}
                      onClick={() => selectDate(date)}
                      disabled={!date}
                      className={`
                        p-2 sm:p-3 text-sm relative hover:bg-primary-50 transition-colors rounded-lg min-h-[44px] touch-manipulation
                        ${!date ? 'invisible' : 'hover:shadow-sm'}
                        ${isToday(date) ? 'bg-blue-50 border-2 border-blue-200' : ''}
                        ${isSelected(date) ? 'bg-primary-100 border-2 border-primary-300' : ''}
                      `}
                    >
                      <span className={`
                        ${isToday(date) ? 'font-bold text-blue-600' : ''}
                        ${isSelected(date) ? 'font-bold text-primary-600' : 'text-secondary-900'}
                      `}>
                        {date ? date.getDate() : ''}
                      </span>

                      {/* Job indicator dots */}
                      {date && hasJobs(date) && (
                        <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 flex gap-0.5">
                          {getJobsForCalendarDate(date).slice(0, 3).map((job, jobIndex) => (
                            <div
                              key={jobIndex}
                              className={`w-1.5 h-1.5 rounded-full ${
                                job.status === 'completed' ? 'bg-green-400' :
                                job.status === 'in-progress' ? 'bg-blue-400' :
                                job.status === 'cancelled' ? 'bg-red-400' : 'bg-yellow-400'
                              }`}
                            />
                          ))}
                          {getJobsForCalendarDate(date).length > 3 && (
                            <div className="w-1.5 h-1.5 rounded-full bg-secondary-400" />
                          )}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Daily Job Modal - opens when clicking a calendar day (quick view / add) */}
      {showDayModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={() => setShowDayModal(false)}>
          <div className="bg-white rounded-t-2xl sm:rounded-xl max-w-2xl w-full max-h-[90vh] sm:max-h-[85vh] overflow-hidden shadow-xl flex flex-col animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-secondary-200 flex items-center justify-between flex-shrink-0 gap-3">
              <div className="min-w-0 flex-1">
                <h2 className="text-xl font-bold text-secondary-900">
                  Jobs for {formatDate(selectedDate)}
                </h2>
                {(() => {
                  const { url, stops } = getMapStopsAndUrl();
                  if (!url) return null;
                  return (
                    <div className="mt-1">
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 text-sm font-medium"
                      >
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary-100 flex-shrink-0">
                          <MapPin className="w-4 h-4" strokeWidth={2.5} />
                        </span>
                        View all on map
                      </a>
                      {stops.length > 0 && (
                        <p className="text-xs text-secondary-600 flex flex-wrap items-center gap-x-2 gap-y-1 mt-1.5">
                          <span className="font-medium text-secondary-700">On map:</span>
                          {stops.map(({ label, customerName }) => (
                            <span key={label} className="inline-flex items-center justify-center w-5 h-5 rounded bg-primary-100 text-primary-700 font-bold text-[10px]">{label}</span>
                          ))}
                        </p>
                      )}
                    </div>
                  );
                })()}
              </div>
              <button
                type="button"
                onClick={() => setShowDayModal(false)}
                className="p-2 text-secondary-500 hover:text-secondary-700 hover:bg-secondary-100 rounded-lg flex-shrink-0"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {showAddFormInDayModal ? (
              /* Add job form inside day modal */
              <form onSubmit={handleAddJobFromDayModal} className="p-6 overflow-y-auto flex-1 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">Customer *</label>
                  <select
                    value={dayModalCustomerId}
                    onChange={(e) => setDayModalCustomerId(e.target.value)}
                    className="input"
                    required
                  >
                    <option value="">Select customer</option>
                    {customers.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.fullName || `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'Unknown'} {c.city ? ` · ${c.city}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">Service type *</label>
                  <select
                    value={newJob.serviceType}
                    onChange={(e) => setNewJob(prev => ({ ...prev, serviceType: e.target.value }))}
                    className="input"
                    required
                  >
                    <option value="">Select</option>
                    {['Lawn Mowing', 'Hedge Trimming', 'Tree Trimming', 'Fertilizing', 'Pre-Emergent / Fertilize', 'Weed Control', 'Aeration', 'Seeding', 'Lighting', 'Planting', 'Other'].map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">What we're doing / Description</label>
                  <textarea
                    value={newJob.description}
                    onChange={(e) => setNewJob(prev => ({ ...prev, description: e.target.value }))}
                    className="input"
                    rows={3}
                    placeholder="Describe the work for this job..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newJob.price}
                    onChange={(e) => setNewJob(prev => ({ ...prev, price: e.target.value }))}
                    className="input"
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">Notes</label>
                  <textarea
                    value={newJob.notes}
                    onChange={(e) => setNewJob(prev => ({ ...prev, notes: e.target.value }))}
                    className="input"
                    rows={2}
                    placeholder="Access, pets, special instructions..."
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={() => setShowAddFormInDayModal(false)} className="btn-secondary">Cancel</button>
                  <button type="submit" className="btn-primary">Add job</button>
                </div>
              </form>
            ) : (
              /* List of jobs for the day - grouped by customer */
              <>
                <div className="p-4 overflow-y-auto flex-1">
                  {selectedDateJobs.length === 0 ? (
                    <p className="text-secondary-500 text-center py-6">No jobs scheduled for this day.</p>
                  ) : (
                    <div className="space-y-4">
                      {(() => {
                        const byCustomer = {};
                        selectedDateJobs.forEach((job) => {
                          const key = job.customerId || job.customerName || job.id;
                          if (!byCustomer[key]) byCustomer[key] = { customerId: job.customerId, customerName: job.customerName, jobs: [] };
                          byCustomer[key].jobs.push(job);
                        });
                        return Object.values(byCustomer).map((group) => {
                          const groupTotal = group.jobs.reduce((sum, j) => sum + (parseFloat(j.price) || 0), 0);
                          const { address, mapUrl } = getCustomerAddress(group.customerId);
                          return (
                          <div key={group.customerId || group.customerName}>
                            {/* Phone: compact card with name, address, map */}
                            <button
                              type="button"
                              onClick={() => {
                                setShowDayModal(false);
                                setCustomerDayModal({ customerId: group.customerId, customerName: group.customerName });
                              }}
                              className="sm:hidden w-full text-left p-4 rounded-xl border border-secondary-200 bg-white hover:bg-primary-50/50 active:bg-primary-100/50 transition-colors shadow-sm"
                              title="Tap to adjust or complete jobs"
                            >
                              <p className="font-semibold text-secondary-900">{group.customerName}</p>
                              <p className="text-sm text-secondary-600 mt-1">
                                {group.jobs.map(j => j.serviceType).filter((v, i, a) => a.indexOf(v) === i).join(', ')}
                              </p>
                              {address && (
                                <p className="text-xs text-secondary-500 mt-2 truncate" title={address}>{address}</p>
                              )}
                              {mapUrl && (
                                <a
                                  href={mapUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="inline-flex items-center gap-2 mt-2 text-primary-600 hover:text-primary-700 text-sm font-medium"
                                >
                                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary-100 flex-shrink-0">
                                    <MapPin className="w-3.5 h-3.5" strokeWidth={2.5} />
                                  </span>
                                  Open in Maps
                                </a>
                              )}
                              <p className="text-xs text-primary-600 mt-2 font-medium">Tap to adjust or complete →</p>
                            </button>
                            {/* Desktop: inline list */}
                            <div className="hidden sm:block">
                              <button
                                type="button"
                                onClick={() => {
                                  setShowDayModal(false);
                                  setCustomerDayModal({ customerId: group.customerId, customerName: group.customerName });
                                }}
                                className="text-left w-full font-semibold text-primary-700 hover:text-primary-800 hover:underline mb-2 p-1 -ml-1 rounded truncate"
                                title={address || group.customerName}
                              >
                                {address || group.customerName} <span className="font-normal text-secondary-500 text-sm">({group.jobs.length} job{group.jobs.length !== 1 ? 's' : ''}<span className="hidden sm:inline"> · ${groupTotal.toFixed(2)}</span>)</span>
                              </button>
                              <ul className="space-y-2">
                                {group.jobs.map((job) => (
                                  <li key={job.id} className="flex items-center justify-between gap-3 py-2 px-3 bg-secondary-50 rounded-lg border border-secondary-100">
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm text-secondary-700">{job.serviceType}<span className="hidden sm:inline">{job.price != null && job.price !== '' ? ` · $${parseFloat(job.price).toFixed(2)}` : ''}</span></p>
                                      {job.notes && <p className="text-xs text-secondary-500 mt-0.5 truncate" title={job.notes}>{job.notes}</p>}
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                      <select
                                        value={job.status}
                                        onChange={(e) => updateJobStatus(job.id, e.target.value)}
                                        className="input text-xs py-1"
                                      >
                                        <option value="scheduled">Scheduled</option>
                                        <option value="in-progress">In progress</option>
                                        <option value="completed">Completed</option>
                                        <option value="cancelled">Cancelled</option>
                                      </select>
                                      <button
                                        type="button"
                                        onClick={() => { setShowDayModal(false); startEditingJob(job); setShowAddJob(true); }}
                                        className="text-primary-600 hover:text-primary-700 text-sm font-medium px-2 py-1 rounded hover:bg-primary-50"
                                      >
                                        Edit
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleDeleteJob(job.id)}
                                        className="text-red-600 hover:text-red-700 text-sm font-medium px-2 py-1 rounded hover:bg-red-50"
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  </li>
                                ))}
                              </ul>
                              <p className="mt-1.5 text-sm text-secondary-600 hidden sm:block">
                                Total: <span className="font-semibold text-green-700">${groupTotal.toFixed(2)}</span>
                              </p>
                            </div>
                          </div>
                          );
                        });
                      })()}
                    </div>
                  )}
                </div>
                <div className="p-4 border-t border-secondary-200 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddFormInDayModal(true);
                      setDayModalCustomerId('');
                      setNewJob(prev => ({ ...prev, serviceType: '', description: '', scheduledDate: selectedDate, price: '', status: 'scheduled', notes: '' }));
                    }}
                    className="w-full btn-primary"
                  >
                    <Plus className="w-5 h-5 mr-2 inline" />
                    Add job for this day
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Customer day – manage one customer's jobs for the day, add more */}
      {customerDayModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-xl max-w-lg w-full max-h-[92vh] overflow-y-auto shadow-xl">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-start gap-3 mb-4">
                <div className="min-w-0 flex-1">
                  {(() => {
                    const { address, mapUrl } = getCustomerAddress(customerDayModal.customerId);
                    return (
                      <>
                        <h2 className="text-xl font-bold text-secondary-900 truncate" title={address || customerDayModal.customerName}>
                          {address || customerDayModal.customerName}
                        </h2>
                        <p className="text-sm text-secondary-500 mt-1">
                          {customerDayModal.customerName} · Jobs for {formatDate(selectedDate)}
                        </p>
                        {mapUrl && (
                          <a
                            href={mapUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 mt-2 text-primary-600 hover:text-primary-700 text-sm font-medium"
                          >
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary-100 flex-shrink-0">
                              <MapPin className="w-3.5 h-3.5" strokeWidth={2.5} />
                            </span>
                            Open in Maps
                          </a>
                        )}
                      </>
                    );
                  })()}
                </div>
                <button
                  type="button"
                  onClick={() => { setCustomerDayModal(null); setCustomerDayAdd({ serviceType: '', price: '' }); }}
                  className="p-2 -mr-2 text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 rounded-lg"
                  aria-label="Close"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-2 mb-4">
                {customerDayJobs.map((job) => {
                  const statusLabel = { scheduled: 'Scheduled', 'in-progress': 'In progress', completed: 'Done', cancelled: 'Cancelled' }[job.status] || job.status;
                  const hasPrice = job.price != null && job.price !== '' && !isNaN(parseFloat(job.price)) && parseFloat(job.price) > 0;
                  const jobStatusColor = job.status === 'completed' ? 'border-l-green-500' :
                    job.status === 'in-progress' ? 'border-l-blue-500' :
                    job.status === 'cancelled' ? 'border-l-red-400' : 'border-l-amber-400';
                  return (
                    <div
                      key={job.id}
                      className={`flex items-stretch rounded-xl border border-secondary-200 bg-white shadow-sm overflow-hidden min-h-[48px] border-l-4 ${jobStatusColor}`}
                    >
                      <button
                        type="button"
                        onClick={() => startEditingJob(job)}
                        className="flex-1 min-w-0 text-left py-3 px-3 sm:p-4 hover:bg-primary-50/50 active:bg-primary-100/50 transition-colors flex flex-col justify-center"
                      >
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-secondary-900 text-base sm:text-[15px] leading-tight truncate flex-1 min-w-0">{job.serviceType}</p>
                          <span className={`sm:hidden shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                            job.status === 'completed' ? 'bg-green-100 text-green-700' :
                            job.status === 'in-progress' ? 'bg-blue-100 text-blue-700' :
                            job.status === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                          }`}>
                            {statusLabel}
                          </span>
                        </div>
                        <p className="text-xs text-secondary-500 mt-0.5">
                          {hasPrice ? `$${parseFloat(job.price).toFixed(2)}` : 'No price'}
                        </p>
                        <span className={`hidden sm:inline-block mt-2 text-xs font-medium px-2 py-0.5 rounded-full ${
                          job.status === 'completed' ? 'bg-green-100 text-green-800' :
                          job.status === 'in-progress' ? 'bg-blue-100 text-blue-800' :
                          job.status === 'cancelled' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'
                        }`}>
                          {statusLabel}
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => updateJobStatus(job.id, job.status === 'completed' ? 'scheduled' : 'completed')}
                        className={`px-3 py-2 text-xs font-semibold border-l border-secondary-100 min-h-[44px] flex items-center justify-center shrink-0 transition-colors touch-manipulation ${
                          job.status === 'completed'
                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                            : 'bg-secondary-50 text-secondary-600 hover:bg-green-50 hover:text-green-700'
                        }`}
                        title={job.status === 'completed' ? 'Undo done' : 'Mark done'}
                      >
                        {job.status === 'completed' ? '✓ Done' : 'Done'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteJob(job.id)}
                        className="p-3 text-secondary-400 hover:bg-red-50 hover:text-red-600 transition-colors border-l border-secondary-100 min-h-[44px] min-w-[44px] flex items-center justify-center shrink-0"
                        title="Delete"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  );
                })}
              </div>

              <form onSubmit={handleAddJobInCustomerDayModal} className="border-t border-secondary-200 pt-4">
                <p className="text-sm font-medium text-secondary-700 mb-2">Add another job for this customer</p>
                <div className="flex flex-wrap gap-2 items-end">
                  <div className="flex-1 min-w-[120px]">
                    <label className="block text-xs text-secondary-500 mb-1">Service</label>
                    <select
                      value={customerDayAdd.serviceType}
                      onChange={(e) => setCustomerDayAdd(prev => ({ ...prev, serviceType: e.target.value }))}
                      className="input w-full text-sm py-2"
                    >
                      <option value="">Select</option>
                      {SERVICE_OPTIONS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div className="w-24">
                    <label className="block text-xs text-secondary-500 mb-1">Price</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0"
                      value={customerDayAdd.price}
                      onChange={(e) => setCustomerDayAdd(prev => ({ ...prev, price: e.target.value }))}
                      className="input w-full text-sm py-2"
                    />
                  </div>
                  <button type="submit" className="btn-primary py-2 px-4">Add job</button>
                </div>
              </form>

              <button
                type="button"
                onClick={() => { setCustomerDayModal(null); setCustomerDayAdd({ serviceType: '', price: '' }); }}
                className="w-full mt-4 btn-secondary py-2"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Job Modal - mobile friendly */}
      {showAddJob && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-xl max-w-2xl w-full max-h-[92vh] overflow-y-auto shadow-xl animate-slide-up">
            <div className={`p-4 sm:p-6 ${editingJob ? 'pb-8' : ''}`}>
              <div className="flex justify-between items-start gap-3 mb-4 sm:mb-6">
                <h2 className="text-lg sm:text-2xl font-bold text-secondary-900 leading-tight">
                  {editingJob ? 'Edit Job' : 'Schedule Jobs for Multiple Customers'}
                </h2>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddJob(false);
                    setEditingJob(null);
                    setNewJob({
                      services: [{ serviceType: '', price: '' }],
                      serviceType: '',
                      description: '',
                      scheduledDate: selectedDate,
                      price: '',
                      status: 'scheduled',
                      notes: ''
                    });
                  }}
                  className="p-2 -mr-2 text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation"
                  aria-label="Close"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={editingJob ? handleEditJob : handleAddJob} className={editingJob ? 'space-y-3 sm:space-y-6' : 'space-y-4 sm:space-y-6'}>
                {/* Customer Selection - only show when adding new jobs */}
                {!editingJob && (
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Select Customers * ({selectedCustomers.length} selected)
                    </label>

                    {/* Search Bar */}
                    <div className="mb-3">
                      <input
                        type="text"
                        placeholder="Search customers by name or email..."
                        value={customerSearch}
                        onChange={(e) => setCustomerSearch(e.target.value)}
                        className="input"
                      />
                    </div>

                    {/* Selected Customers Summary */}
                    {selectedCustomers.length > 0 && (
                      <div className="mb-3 p-3 bg-primary-50 rounded-lg">
                        <div className="text-sm font-medium text-primary-700 mb-2">
                          Selected Customers:
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {selectedCustomers.map(customerId => {
                            const customer = customers.find(c => c.id === customerId);
                            return (
                              <span
                                key={customerId}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-primary-100 text-primary-700"
                              >
                                {customer ? (customer.fullName || `${customer.firstName || ''} ${customer.lastName || ''}`.trim()) : 'Unknown'}
                                <button
                                  type="button"
                                  onClick={() => handleCustomerToggle(customerId)}
                                  className="ml-1 text-primary-600 hover:text-primary-800"
                                >
                                  ×
                                </button>
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Customer List */}
                    <div className="max-h-60 overflow-y-auto border border-secondary-200 rounded-lg">
                      {filteredCustomers.length === 0 ? (
                        <div className="p-4 text-center text-secondary-500">
                          {customerSearch ? 'No customers found matching your search.' : 'No customers available.'}
                        </div>
                      ) : (
                        filteredCustomers.map((customer) => (
                          <label
                            key={customer.id}
                            className={`flex items-center p-3 cursor-pointer hover:bg-secondary-50 transition-colors border-b border-secondary-100 last:border-b-0 ${
                              selectedCustomers.includes(customer.id) ? 'bg-primary-50' : ''
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedCustomers.includes(customer.id)}
                              onChange={() => handleCustomerToggle(customer.id)}
                              className="mr-3 text-primary-600 focus:ring-primary-500 rounded"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-secondary-900">
                                {customer.fullName || `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || 'Unknown Customer'}
                              </div>
                              <div className="text-sm text-secondary-600">
                                {customer.email} • {customer.phone || 'No phone'}
                              </div>
                              {customer.address && (
                                <div className="text-xs text-secondary-500 mt-1">
                                  {typeof customer.address === 'object'
                                    ? `${customer.address.street || ''}, ${customer.address.city || ''}, ${customer.address.state || ''} ${customer.address.zipCode || ''}`.replace(/^, |, $/, '').trim()
                                    : customer.address
                                  }
                                </div>
                              )}
                            </div>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Current Customer - editable when editing */}
                {editingJob && (
                  <div className="py-2.5 px-3 sm:p-4 bg-secondary-50 rounded-lg space-y-3">
                    <p className="text-xs sm:text-sm font-medium text-secondary-600">Customer Details</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-secondary-600 mb-1">First Name</label>
                        <input
                          type="text"
                          value={editingCustomerInfo.firstName || ''}
                          onChange={(e) => setEditingCustomerInfo({ ...editingCustomerInfo, firstName: e.target.value })}
                          className="input text-sm py-2"
                          placeholder="First name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-secondary-600 mb-1">Last Name</label>
                        <input
                          type="text"
                          value={editingCustomerInfo.lastName || ''}
                          onChange={(e) => setEditingCustomerInfo({ ...editingCustomerInfo, lastName: e.target.value })}
                          className="input text-sm py-2"
                          placeholder="Last name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-secondary-600 mb-1">Email</label>
                        <input
                          type="email"
                          value={editingCustomerInfo.email || ''}
                          onChange={(e) => setEditingCustomerInfo({ ...editingCustomerInfo, email: e.target.value })}
                          className="input text-sm py-2"
                          placeholder="Email"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-secondary-600 mb-1">Phone</label>
                        <input
                          type="tel"
                          value={editingCustomerInfo.phone || ''}
                          onChange={(e) => setEditingCustomerInfo({ ...editingCustomerInfo, phone: e.target.value })}
                          className="input text-sm py-2"
                          placeholder="Phone"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs font-medium text-secondary-600 mb-1">City</label>
                        <input
                          type="text"
                          value={editingCustomerInfo.city || ''}
                          onChange={(e) => setEditingCustomerInfo({ ...editingCustomerInfo, city: e.target.value })}
                          className="input text-sm py-2"
                          placeholder="City"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs font-medium text-secondary-600 mb-1">Address</label>
                        <textarea
                          value={editingCustomerInfo.address || ''}
                          onChange={(e) => setEditingCustomerInfo({ ...editingCustomerInfo, address: e.target.value })}
                          className="input text-sm py-2"
                          placeholder="Full address"
                          rows={2}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Services: multiselect + price per line + total when adding; single when editing */}
                {editingJob ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1.5">Service *</label>
                      <select
                        value={newJob.serviceType}
                        onChange={(e) => setNewJob({ ...newJob, serviceType: e.target.value })}
                        className="input w-full py-2.5"
                        required
                      >
                        <option value="">Select service</option>
                        {SERVICE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1.5">Price</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-500">$</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={newJob.price}
                          onChange={(e) => setNewJob({...newJob, price: e.target.value})}
                          className="input pl-8 py-2 sm:py-2.5 w-full"
                          placeholder="0"
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <label className="block text-sm font-medium text-secondary-700">Services & price each *</label>
                      <button
                        type="button"
                        onClick={addServiceLine}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        Add service
                      </button>
                    </div>
                    <p className="text-xs text-secondary-500 mb-2">Pick a service and enter price (or leave $0 to use customer default). Add multiple rows for multiple services.</p>
                    <div className="space-y-2 border border-secondary-200 rounded-xl overflow-hidden bg-secondary-50/30">
                      {(newJob.services || []).map((line, index) => (
                        <div key={index} className="flex flex-wrap items-center gap-2 p-2 sm:p-2.5 bg-white border-b border-secondary-100 last:border-b-0">
                          <select
                            value={line.serviceType}
                            onChange={(e) => updateServiceLine(index, 'serviceType', e.target.value)}
                            className="input py-2 flex-1 min-w-0 text-sm"
                          >
                            <option value="">Select service</option>
                            {SERVICE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                          <div className="relative w-24 shrink-0">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-secondary-500 text-sm">$</span>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={line.price}
                              onChange={(e) => updateServiceLine(index, 'price', e.target.value)}
                              className="input pl-6 py-2 w-full text-sm"
                              placeholder="0"
                            />
                          </div>
                          <button type="button" onClick={() => removeServiceLine(index)} className="p-2 text-secondary-400 hover:text-red-600 shrink-0" aria-label="Remove line" title="Remove this service">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <p className="mt-2 text-right text-sm font-semibold text-secondary-900 border-t border-secondary-200 pt-2">
                      Total: ${((newJob.services || []).reduce((sum, l) => sum + (parseFloat(l.price) || 0), 0)).toFixed(2)}
                    </p>
                  </div>
                )}

                {/* Job Description - fewer rows on mobile when editing */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1.5 sm:mb-2">
                    Description
                  </label>
                  <textarea
                    value={newJob.description}
                    onChange={(e) => setNewJob({...newJob, description: e.target.value})}
                    className="input"
                    rows={editingJob ? 2 : 4}
                    placeholder="Optional"
                  />
                </div>

                {/* Date + Status: one row on mobile when editing */}
                <div className={`grid gap-3 sm:gap-6 ${editingJob ? 'grid-cols-2 gap-2 sm:gap-6' : 'grid-cols-1 md:grid-cols-2'}`}>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1 sm:mb-2">
                      Date *
                    </label>
                    <input
                      type="date"
                      value={newJob.scheduledDate}
                      onChange={(e) => setNewJob({...newJob, scheduledDate: e.target.value})}
                      className="input py-2 sm:py-2.5"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1 sm:mb-2">
                      Status
                    </label>
                    <select
                      value={newJob.status}
                      onChange={(e) => setNewJob({...newJob, status: e.target.value})}
                      className="input py-2 sm:py-2.5"
                    >
                      <option value="scheduled">Scheduled</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="in-progress">In Progress</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                </div>

                {/* Notes - compact when editing */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1.5 sm:mb-2">
                    Notes
                  </label>
                  <textarea
                    value={newJob.notes}
                    onChange={(e) => setNewJob({...newJob, notes: e.target.value})}
                    className="input py-2 sm:py-2.5"
                    rows={editingJob ? 2 : 3}
                    placeholder="Access, pets, instructions..."
                  />
                </div>

                <div className="flex flex-wrap items-center justify-between sm:justify-end gap-2 pt-4 border-t border-secondary-200">
                  {editingJob ? (
                    <button
                      type="button"
                      onClick={() => { if (window.confirm('Delete this job?')) { handleDeleteJob(editingJob.id); setShowAddJob(false); setEditingJob(null); } }}
                      className="text-red-600 hover:text-red-700 text-sm font-medium py-2 sm:order-last sm:mr-auto"
                    >
                      Delete
                    </button>
                  ) : null}
                  <div className="flex space-x-2 w-full sm:w-auto">
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddJob(false);
                        setEditingJob(null);
                        setSelectedCustomers([]);
                        setCustomerSearch('');
                        setNewJob({
                          services: [{ serviceType: '', price: '' }],
                          serviceType: '',
                          description: '',
                          scheduledDate: selectedDate,
                          price: '',
                          status: 'scheduled',
                          notes: ''
                        });
                      }}
                      className="btn-secondary flex-1 sm:flex-none"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn-primary flex-1 sm:flex-none"
                    >
                      {editingJob ? 'Update' : 'Schedule Job'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Map modal */}
      {mapModalJob && (() => {
        const customer = customers.find(c => c.id === mapModalJob.customerId);
        const addressForMap = (mapModalAddress && mapModalAddress.trim()) || mapModalJob.customerName || '';
        const mapQuery = encodeURIComponent(addressForMap);
        const mapUrl = `https://www.google.com/maps?q=${mapQuery}`;
        const embedUrl = addressForMap
          ? `https://www.google.com/maps?q=${mapQuery}&output=embed`
          : '';
        const handleSaveAddress = async () => {
          if (!customer) return;
          const trimmed = mapModalAddress.trim();
          setMapModalSaving(true);
          try {
            const addressPayload = trimmed
              ? { street: trimmed, city: '', state: '', zipCode: '' }
              : null;
            await updateDoc(doc(db, 'customers', customer.id), { address: addressPayload });
          } catch (err) {
            console.error('Failed to save address:', err);
            alert('Failed to save address. Please try again.');
          } finally {
            setMapModalSaving(false);
          }
        };
        return (
          <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={() => setMapModalJob(null)}>
            <div
              className="bg-white rounded-t-2xl sm:rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-xl animate-slide-up"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-between items-start gap-3 p-4 border-b border-secondary-200">
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg font-bold text-secondary-900">{mapModalJob.customerName}</h2>
                  <p className="text-sm text-secondary-600 mt-0.5">{mapModalJob.serviceType}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setMapModalJob(null)}
                  className="p-2 text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 rounded-lg shrink-0"
                  aria-label="Close"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="flex-1 min-h-0 flex flex-col p-4 overflow-y-auto">
                <label className="block text-sm font-medium text-secondary-700 mb-1.5">Address</label>
                <input
                  type="text"
                  value={mapModalAddress}
                  onChange={(e) => setMapModalAddress(e.target.value)}
                  placeholder="Street, city, state, ZIP"
                  className="input w-full mb-3"
                />
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <button
                    type="button"
                    onClick={handleSaveAddress}
                    disabled={mapModalSaving}
                    className="btn-primary"
                  >
                    {mapModalSaving ? 'Saving…' : 'Save address'}
                  </button>
                </div>
                {embedUrl ? (
                  <iframe
                    title="Job location map"
                    src={embedUrl}
                    className="w-full h-[280px] sm:h-[400px] rounded-lg border border-secondary-200"
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                ) : (
                  <a
                    href={mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-3 rounded-lg bg-primary-100 text-primary-700 font-medium hover:bg-primary-200 transition-colors"
                  >
                    <MapPin className="w-5 h-5" />
                    Open in Google Maps
                  </a>
                )}
                {embedUrl && (
                  <a
                    href={mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-3 text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Open in Google Maps ↗
                  </a>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default DailyJobs;